/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { isRequestFailure, useFetch } from '../../../hooks/useFetch';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  FormActions,
  FormRow,
  FormSection,
  FormShell,
} from '../../ui';
import { DateInputForm } from '../../inputs/forms/DateInputForm';
import { CustomParentsInputForm } from '../../inputs/forms/CustomParentsInputForm';

interface StudentFormProps {
  defaultValues?: any;
  show?: boolean;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function StudentForm({ defaultValues = {}, show = false }: StudentFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/children';
  const { requestApi } = useFetch(baseUrl);
  const { itemId, institutionId, teamId } = useParams();
  const navigate = useNavigate();

  // REQ-004: Buscar informações do Team para exibir o professor responsável
  const [teamInfo, setTeamInfo] = React.useState<any>(null);
  const { requestApi: requestTeamApi } = useFetch('/v1/admin/teams');

  React.useEffect(() => {
    if (teamId && !defaultValues.name) {
      const fetchTeamInfo = async () => {
        const { data } = await requestTeamApi('get', null, `/v1/admin/teams/${teamId}`);
        if (data?.data) {
          setTeamInfo(data.data);
        }
      };
      fetchTeamInfo();
    }
  }, [teamId]);

  const optionsKinship = [
    {
      label: 'Pai',
      value: 'PAI',
    },
    {
      label: 'Mãe',
      value: 'MAE',
    },
    {
      label: 'Tio',
      value: 'TIO',
    },
    {
      label: 'Tia',
      value: 'TIA',
    },
    {
      label: 'Avô(ó)',
      value: 'AVO',
    },
    {
      label: 'Outro',
      value: 'OUTRO',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do aluno',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'birth_date',
      value: defaultValues.birth_date ? (moment(defaultValues.birth_date).toDate() as any) : null,
      placeholder: '00/00/0000',
      label: 'Data de nascimento',
      yup: Yup.string().nullable().required('Este campo é obrigatório!'),
    },
    {
      name: 'parents',
      value: [
        ...(defaultValues?.parents?.map((item) => ({
          ...item,
          kinship: optionsKinship.find((kinship) => kinship.value === item.kinship),
        })) ?? []),
      ],
      placeholder: 'Responsáveis do aluno',
      label: 'Responsáveis',
      yup: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().required('Este campo é obrigatório!'),
            email: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
            cell_phone: Yup.string().required('Este campo é obrigatório!'),
            kinship: Yup.mixed().required('Este campo é obrigatório!'),
          })
        )
        .min(1, 'É necessário informar pelo menos 1 responsável do aluno!'),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const birthDate = formInputs.find((field) => field.name === 'birth_date');
  const parents = formInputs.find((field) => field.name === 'parents');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      institution_id: defaultValues.name ? _ : institutionId,
      team_id: defaultValues.name ? _ : teamId,
      birth_date: values.birth_date ? moment(values.birth_date).format('yyyy-MM-DD') : null,
      parents: [
        ...(values.parents.map((parentItem) => ({
          name: parentItem.name,
          email: parentItem.email,
          cell_phone: parentItem.cell_phone,
          kinship: parentItem.kinship.value,
        })) ?? []),
      ],
    };
    const result = await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if (!isRequestFailure(result))
      navigate(`/institutions/list/${institutionId}/teams/${teamId}/students`);
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  const backTo = `/institutions/list/${institutionId}/teams/${teamId}/students`;
  let titleAction = 'Criar';
  if (show) titleAction = 'Visualizar';
  else if (defaultValues.name) titleAction = 'Editar';

  return (
    <FormShell title={`${titleAction} Aluno`} backTo={backTo} backLabel="Alunos">
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações básicas">
          {teamInfo?.teacher && (
            <Alert variant="info">
              <AlertTitle>Professor responsável pela turma</AlertTitle>
              <AlertDescription>{teamInfo.teacher.name}</AlertDescription>
            </Alert>
          )}
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled={show}
              register={register}
              errors={errors}
            />
            <DateInputForm
              name={birthDate?.name}
              label={birthDate?.label}
              placeholderText={birthDate?.placeholder}
              isRequired
              disabled={show}
              minDate={null}
              maxDate={new Date()}
              control={control}
              errors={errors}
            />
          </FormRow>
        </FormSection>

        <FormSection title="Responsáveis">
          <CustomParentsInputForm
            name={parents?.name}
            optionsKinship={optionsKinship}
            setFormValue={setValue}
            register={register}
            isDisabled={show}
            control={control}
            watch={watch}
            errors={errors}
          />
        </FormSection>

        {!show && <FormActions cancelTo={backTo} isLoading={isSubmitting} />}
      </Box>
    </FormShell>
  );
}
